/**
 * BoardRow — one conversation on the v2 board (CRM UI streamline D-8).
 *
 * The classic list row is three stacked lines and ~72px tall, which is why a
 * busy board scrolls forever. The first slim replacement was ONE line at `lg`
 * (h-12), and it failed in the field on day one (owner, 2026-08-06: "I don't
 * know who is who") — in a 320px column the name shared its line with the
 * status pill, the flag chip and the snippet, and the NAME was what got
 * truncated to three letters. This is the corrected shape: the SAME two-line
 * block at every width. Line 1 is WHO — the name is the only flexible thing
 * on it, so it always wins — plus the attention marks (draft dot, flag count)
 * and the time at the edge. Line 2 is the state · the study · who said the
 * last thing. The pills are still the classic row's pills, unchanged — a
 * recruiter who learned the amber flag count and the blue draft dot meets the
 * same marks here, one line lower.
 *
 * FIXED HEIGHT IS A CONTRACT, not a style choice: react-window measures
 * nothing, it multiplies. `ROW_HEIGHT` and the `h-16` class below are the
 * same number said twice — change one, change both. (There used to be a
 * second, `lg`-only number; both tiers share the two-line block now.)
 *
 * Navigation is the CRM's row idiom, not a bespoke one: the name is a real
 * <a> (RowLink) to the lead page so right-click → "Open in new tab", hover
 * preview and Ctrl/Cmd+click work exactly like every other list in the module,
 * while the row itself is a role=button that SELECTS the conversation (the
 * board's own verb) on a plain click or Enter/Space. Modified and middle
 * clicks fall through to useRowNav's window.open — the row never
 * preventDefaults an aux click, so a new tab always opens exactly once.
 */
import { memo, useCallback, useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { ShieldAlert } from 'lucide-react';
import type { BoardRow as BoardRowData } from '../../AgentConsole';
import { RowLink, useRowNav } from '../../../../components/shared';
import { STATUS_PILL, ago, timeShort } from '../../../../components/agent/shared';

/** h-16 — the two-line row, every width. */
export const ROW_HEIGHT = 64;

/**
 * The row height react-window must use. Kept as a hook so the call sites'
 * shape survives the one-height unification — if a breakpoint-dependent
 * height ever returns, only this body changes (its old matchMedia shape now
 * lives on in useLgUp).
 */
export function useBoardRowHeight(): number {
  return ROW_HEIGHT;
}

export interface BoardRowProps {
  row: BoardRowData;
  selected: boolean;
  /** A draft is waiting on this conversation (the lane owns the lookup). */
  hasDraft: boolean;
  /**
   * Memo-invalidation tick, deliberately NOT read in the markup (the classic
   * list row's exact pattern): `ago()` reads the ambient clock at render time,
   * and without this prop a memoized row would freeze its relative timestamp
   * between refetches — seam-4 F1.
   */
  nowTs: number;
  onSelect: (id: string) => void;
  /**
   * Let the row scroll itself into view when it becomes selected. Virtualized
   * lanes pass false — their FixedSizeList owns scrolling, and a selected row
   * re-mounting in overscan must not tug the lane (seam-4 F2).
   */
  scrollOnSelect?: boolean;
  /** react-window's absolute positioning — absent when the lane renders plain. */
  style?: CSSProperties;
}

function BoardRowImpl({ row, selected, hasDraft, onSelect, scrollOnSelect = true, style }: BoardRowProps) {
  const rowNav = useRowNav();
  const nav = rowNav(`/crm/contacts/${row.contact_id}`);
  const ref = useRef<HTMLDivElement>(null);

  // A ?conversation= deep link can arrive with its row far down a lane (or
  // just below the card's fold). `nearest` scrolls the least that works and
  // is a no-op when the row is already on screen — including when the lane's
  // virtualizer has just scrolled it into the window itself.
  useEffect(() => {
    if (selected && scrollOnSelect) ref.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selected, scrollOnSelect]);

  const select = useCallback(() => onSelect(row.id), [onSelect, row.id]);

  const name = row.contact_name || 'Lead';
  const msg = row.last_message;
  const inbound = msg?.direction === 'inbound';

  const when = msg ? (
    <span
      title={timeShort(msg.occurred_at)}
      className="flex-shrink-0 text-[10px] tabular-nums text-gray-400 dark:text-gray-500"
    >
      {ago(msg.occurred_at)}
    </span>
  ) : null;

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-current={selected || undefined}
      aria-label={`${name} — open this conversation`}
      data-convo-id={row.id}
      style={style}
      onClick={(e) => {
        if (e.defaultPrevented) return; // the name link already handled it
        // Match the browser's modified-click contract on the row's implicit
        // target (the lead page) instead of swallowing it into a selection.
        if (e.ctrlKey || e.metaKey) { nav.onClick(e); return; }
        select();
      }}
      onAuxClick={nav.onAuxClick}
      onKeyDown={(e) => {
        // Only when the row itself is focused — Enter/Space on the name link
        // must stay the link's.
        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
          if (e.key === ' ') e.preventDefault(); // don't scroll the card
          select();
        }
      }}
      className={`flex h-16 w-full cursor-pointer items-center gap-2 border-b border-gray-100 px-3 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:border-gray-700/60 ${
        selected
          ? 'bg-primary-50 dark:bg-primary-900/20'
          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
      }`}
    >
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
        {/* Line 1 — WHO. The name is the only flexible element here, so it
            always wins the width; the attention marks and the time are
            fixed-size edge furniture. */}
        <span className="flex min-w-0 items-center gap-1.5">
          <RowLink
            to={`/crm/contacts/${row.contact_id}`}
            title={`${name} — open the full lead page`}
            className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 hover:text-primary-700 dark:text-gray-100 dark:hover:text-primary-400"
          >
            {name}
          </RowLink>
          {hasDraft && (
            <span
              title="A draft is waiting for approval"
              className="h-2 w-2 flex-shrink-0 rounded-full bg-primary-500"
            >
              {/* An 8px dot whose only explanation was a `title` — invisible to
                  a screen reader, and unreachable by touch (no hover). */}
              <span className="sr-only">Draft waiting for approval</span>
            </span>
          )}
          {row.open_flags > 0 && (
            <span
              title={`${row.open_flags} open flag${row.open_flags === 1 ? '' : 's'} on this conversation`}
              className="inline-flex flex-shrink-0 items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
            >
              <ShieldAlert className="h-3 w-3" />{row.open_flags}
            </span>
          )}
          {when}
        </span>

        {/* Line 2 — the state (the classic pills, learned vocabulary), the
            study, and who said the last thing. Muted so line 1 stays the
            anchor; the snippet is the flexible tail. */}
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className={`inline-flex flex-shrink-0 items-center whitespace-nowrap rounded-full px-1.5 py-px text-[10px] font-medium ${STATUS_PILL[row.status] || STATUS_PILL.closed}`}
          >
            {row.status.replace('_', ' ')}
          </span>
          {row.study_name && (
            <span
              title={row.study_name}
              className="min-w-0 max-w-[8rem] flex-shrink-0 truncate text-[11px] text-gray-500 dark:text-gray-400"
            >
              {row.study_name}
            </span>
          )}
          <span className="flex min-w-0 flex-1 items-center gap-1">
            {msg ? (
              <>
                <span
                  className={`flex-shrink-0 text-[9px] font-semibold uppercase ${inbound ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}
                >
                  {inbound ? 'Lead' : 'Team'}
                </span>
                <span className="truncate text-xs text-gray-500 dark:text-gray-400">{msg.body}</span>
              </>
            ) : (
              <span className="truncate text-xs text-gray-400 dark:text-gray-500">No messages yet</span>
            )}
          </span>
        </span>
      </div>
    </div>
  );
}

/**
 * Memoized because a lane re-renders on every 15s clock tick and every 30s
 * poll, and the virtualized lanes re-render their whole window on every scroll
 * frame. The board hands down a stable `onSelect`, so a row only re-renders
 * when its own data, selection or position actually changed.
 */
export default memo(BoardRowImpl);
